import { DatePipe } from '@angular/common';
import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { format, intlFormatDistance } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  shareReplay,
  Subject,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import {
  getFirstShift,
  getLastShift,
} from 'server/shared/helpers/eligibilityChecks';
import { formatTimeNumber } from 'server/shared/helpers/time';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { PartialAcceptTime } from 'server/shared/models/partialAcceptTime';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser } from 'server/shared/models/user';
import { OrganisationService } from 'src/app/services/organisation.service';
import { downloadJobICS } from '../../../helpers/downloadJobICS';
import { Column } from '../../../helpers/job/jobColumns';
import { TranslationService } from '../../services/translation.service';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation.component';
import { PartialAcceptDialog } from '../partial-accept-dialog/partial-accept-dialog.component';
import { SubscriptionService } from '../subscription.service';

@Component({
  selector: 'app-pooler-job-list-item',
  templateUrl: './pooler-job-list-item.component.html',
  styleUrls: [
    './pooler-job-list-item.component.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
})
export class PoolerJobListItemComponent implements OnInit, OnDestroy {
  i18n = this.translationService.getTranslations('jobs');
  job$ = new BehaviorSubject<Job | undefined>(undefined);
  allowPartial? = false;

  now = new Date();

  @Input() isPossibleJob = false;
  @Input() compact?: boolean;
  @Input() withDate = false;
  @Input() columns: Column[] = [];

  @Input() set job(job: Job | undefined) {
    this.job$.next(job);
    if (job) {
      this.allowPartial = Customers.findOne(job?.customer.id)?.partialAccept;
    }
  }
  get job(): Job | undefined {
    return this.job$.getValue();
  }
  dateAndDistance$: Observable<{ date: string; distance: string } | undefined> =
    this.job$.pipe(
      map((j) => {
        if (!j || !j.yourself) return;
        const date = new Date(
          (j.yourself.updatedAt || j.yourself.createdAt) * 1000,
        );
        return {
          date: format(date, 'dd.MM.yyyy HH:mm', { locale: de }),
          distance: intlFormatDistance(date, new Date(), { locale: 'de' }),
        };
      }),
      shareReplay(1),
    );

  poolerJobStatus$: Observable<InviteeStatusEnum | undefined> = combineLatest([
    this.job$,
  ]).pipe(
    map(([job]) => {
      return job?.yourself?.status;
    }),
    shareReplay(1),
  );

  loggedInUser?: PoolerUser;
  customerOfJob?: Customer;
  departmentOfJob?: Department;

  isAutoBookJob?: boolean;

  destroyed$ = new Subject<void>();

  private userTracker?: Tracker.Computation;

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private subscriptionService: SubscriptionService,
    private organisationService: OrganisationService,
  ) {
    this.userTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        let user = Users.findOne(Meteor.userId());
        if (user?.role === RoleEnum.POOL) {
          this.loggedInUser = user;
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.customerOfJob = Customers.findOne(this.job?.yourself?.customer.id);

        if (this.customerOfJob?.autoBookInvitees) {
          if (this.job?.yourself?.selfSignIn) {
            if (this.customerOfJob?.autoBookSelfSignIns) {
              this.isAutoBookJob =
                this.departmentOfJob?.customerInformation?.[
                  this.customerOfJob?._id
                ]?.autoBookSelfSignIns !== undefined
                  ? this.departmentOfJob?.customerInformation[
                      this.customerOfJob._id
                    ].autoBookSelfSignIns
                  : this.customerOfJob?.autoBookSelfSignInsDepartmentDefault;
            }
          } else {
            this.isAutoBookJob =
              this.departmentOfJob?.customerInformation?.[
                this.customerOfJob?._id
              ]?.autoBookInvitees !== undefined
                ? this.departmentOfJob?.customerInformation[
                    this.customerOfJob._id
                  ].autoBookInvitees
                : this.customerOfJob?.autoBookInviteesDepartmentDefault;
          }
        }
      });
    });

    let departmentAndOrganisation =
      await this.organisationService.getOrganisationAndDepartmentById(
        this.job?.department.id || '',
      );
    if (!departmentAndOrganisation) return;
    this.departmentOfJob = departmentAndOrganisation.department;
  }

  ngOnDestroy() {
    if (this.userTracker) {
      this.userTracker.stop();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onAcceptClick(): void {
    if (this.job?.yourself?.status === InviteeStatusEnum.POSSIBLE_CANDIDATE) {
      return;
    }

    if (this.isAutoBookJob) {
      this.openDirectBookingConfirmDialog();
    } else {
      if (this.job?.yourself?.selfSignIn) {
        this.openAcceptConfirmDialog();
      } else {
        this.acceptJob();
      }
    }
  }

  onPartialAcceptClick(): void {
    if (this.job?.yourself?.status === InviteeStatusEnum.POSSIBLE_CANDIDATE) {
      return;
    }
    this.openPartialAcceptConfirmDialog();
  }

  onDeclineClick(): void {
    if (this.job?.yourself?.status === InviteeStatusEnum.POSSIBLE_CANDIDATE) {
      return;
    }
    this.declineJob();
  }

  private acceptJob(): void {
    if (!this.job) return;
    Meteor.call('acceptJob', this.job._id, (error: Meteor.Error) => {
      if (error?.error) throw new Meteor.Error(error.error);
    });
  }

  private partialAcceptJob(times: PartialAcceptTime[]): void {
    if (!this.job) return;
    Meteor.call('acceptJob', this.job._id, times, (error: Meteor.Error) => {
      if (error?.error) throw new Meteor.Error(error.error);
    });
  }

  private declineJob(): void {
    if (!this.job) return;
    Meteor.call('declineJob', this.job._id, (error: Meteor.Error) => {
      if (error?.error) throw new Meteor.Error(error.error);
    });
  }

  hideJob(): void {
    if (!this.job) return;
    Meteor.call('hideJob', this.job._id, (error: Meteor.Error) => {
      if (error?.error) throw new Meteor.Error(error.error);
    });
  }

  openAcceptConfirmDialog(event?: Event): void {
    if (!this.job) return;
    event?.stopPropagation();
    let text = '';
    if (this.job.ownAvailabilityChecked) {
      text =
        (this.i18n?.confirmJobAcceptAvailablePoolerPre ||
          'Möchtest du dich für den Dienst am') +
        ' ' +
        this.datePipe.transform(this.job.date) +
        ' ' +
        (this.i18n?.confirmJobAcceptAvailablePoolerPost || 'bewerben?');
    } else {
      text =
        (this.i18n?.confirmJobAcceptUnavailablePoolerPre ||
          'Stelle sicher, dass du am') +
        ' ' +
        this.datePipe.transform(this.job.date) +
        ' ' +
        (this.i18n?.from || 'von') +
        ' ' +
        formatTimeNumber(getFirstShift(this.job.shifts).from) +
        ' ' +
        (this.i18n?.to || 'bis') +
        ' ' +
        formatTimeNumber(getLastShift(this.job.shifts).to) +
        ' ' +
        (this.i18n?.confirmJobAcceptUnavailablePoolerPost ||
          ' verfügbar bist. Möchtest du dich für diesen Dienst bewerben?');
    }
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id + '-confirm-accept',
        text,
        defaultImpolite: true,
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        this.acceptJob();
      }
    });
  }

  openDirectBookingConfirmDialog(event?: Event): void {
    if (!this.job) return;
    event?.stopPropagation();
    let text =
      this.i18n?.confirmAutoBookInviteeAcceptJob ||
      'Die Zusage ist verbindlich und kann nicht mehr geändert werden.';

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: this.job._id + '-confirm-accept',
        text,
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        this.acceptJob();
      }
    });
  }

  openPartialAcceptConfirmDialog(event?: Event): void {
    if (!this.job) return;
    event?.stopPropagation();
    let data = {
      shifts: this.job.shifts,
      partialTimes: [] as PartialAcceptTime[],
    };
    if (
      this.job?.yourself?.status === InviteeStatusEnum.PARTIAL_AVAILABLE &&
      this.job.yourself?.partialTimes
    ) {
      data.partialTimes = this.job.yourself.partialTimes;
    }
    const dialogRef = this.dialog.open(PartialAcceptDialog, {
      data: data,
    });
    dialogRef.afterClosed().subscribe((result: PartialAcceptTime[]) => {
      if (result && result.length > 0) {
        this.partialAcceptJob(result);
      }
    });
  }

  downloadICS() {
    if (!this.job?.yourself) return;
    downloadJobICS(this.job);
  }

  get hasHoursToRecord(): boolean {
    return !!this.job?.yourself?.timeTrackings?.some(
      (timeTracking) =>
        !timeTracking.toBeApprovedAt &&
        !timeTracking.approvedAt &&
        !timeTracking.disabled,
    );
  }

  readonly JobStatusEnum = JobStatusEnum;
  readonly InviteeStatusEnum = InviteeStatusEnum;
}
